
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import 'lodash/isEqual';
import 'just-kebab-case';
import { k as keyCodes } from './labels-mixin.js';
import PendoTabPanel from './pendo-tab-panel.js';

const TAB_THEME_COLOR_MAP = {
  designer: '#128297',
  via: '#2A2C35',
  app: '#2A2C35'
};
var script = {
  name: 'PendoTabs',
  components: {
    PendoTabPanel
  },
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: {
      type: String,
      default: undefined,
      required: true
    },
    tabs: {
      type: Array,
      default: () => []
    },
    theme: {
      type: String,
      default: 'app',
      validator: theme => ['app', 'designer', 'via'].includes(theme)
    },
    card: {
      type: Boolean,
      default: false
    },
    vertical: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      focusOnNextModelUpdate: false
    };
  },

  watch: {
    value() {
      const tabIndex = this.tabs.findIndex(tab => tab.prop === this.value);
      const activeTabNeedsFocus = document.activeElement !== this.$refs.tab[tabIndex];

      if (this.focusOnNextModelUpdate || activeTabNeedsFocus) {
        this.$refs.tab[tabIndex].focus();
        this.focusOnNextModelUpdate = false;
      }
    }

  },

  created() {
    if (!this.value) {
      this.activateTab(this.tabs[0], true);
    }
  },

  mounted() {
    this.setActiveTabColor();
  },

  methods: {
    onTabClick(tab) {
      if (tab.disabled) {
        return;
      }

      this.activateTab(tab, false);
      this.$emit('tab-click', tab);
    },

    onTabKeydown(event, tab) {
      const key = event.keyCode;

      switch (key) {
        case keyCodes.end:
          event.preventDefault();
          const lastTab = this.tabs[this.tabs.length - 1];

          if (lastTab.lazy) {
            this.focusLastTab();
          } else {
            this.activateTab(lastTab, true);
          }

          break;

        case keyCodes.home:
          event.preventDefault();
          const firstTab = this.tabs[0];

          if (firstTab.lazy) {
            this.focusFirstTab();
          } else {
            this.activateTab(firstTab, true);
          }

          break;

        case keyCodes.up:
        case keyCodes.down:
          if (this.vertical) {
            event.preventDefault();
            this.switchTabOnArrowPress(event, tab);
          }

          break;
      }
    },

    onTabKeyup(event, tab) {
      switch (event.keyCode) {
        case keyCodes.left:
        case keyCodes.right:
          if (!this.vertical) {
            this.switchTabOnArrowPress(event, tab);
          }

          break;

        case keyCodes.enter:
        case keyCodes.space:
          this.activateTab(tab, true);
          break;
      }
    },

    onTabFocus(event, tab) {
      if (tab.disabled) {
        return;
      }

      if (event.target === document.activeElement) {
        if (tab.lazy) {
          return;
        }

        this.activateTab(tab, false);
      }
    },

    switchTabOnArrowPress(event, tab) {
      const navigationKeyCodes = {
        37: -1,
        38: -1,
        39: 1,
        40: 1
      };

      if (!navigationKeyCodes[event.keyCode]) {
        return;
      }

      const prop = tab.prop;
      const index = this.tabs.findIndex(tab => tab.prop === prop);
      let nextIndex = parseInt(index, 10) + navigationKeyCodes[event.keyCode];

      while (this.tabs[nextIndex] && this.tabs[nextIndex].disabled) {
        nextIndex += navigationKeyCodes[event.keyCode];
      }

      if (this.$refs.tab[nextIndex]) {
        this.$refs.tab[nextIndex].focus();
        return;
      }

      switch (event.keyCode) {
        case keyCodes.left:
        case keyCodes.up:
          this.focusLastTab();
          break;

        case keyCodes.right:
        case keyCodes.down:
          this.focusFirstTab();
      }
    },

    focusFirstTab() {
      this.$refs.tab[0].focus();
    },

    focusLastTab() {
      this.$refs.tab[this.tabs.length - 1].focus();
    },

    activateTab(tab, setFocus) {
      if (tab.prop !== this.value) {
        this.$emit('change', tab.prop);
        this.focusOnNextModelUpdate = setFocus;
      }
    },

    setActiveTabColor() {
      const theme = this.theme,
            activeTabColor = this.activeTabColor;
      const colorToApply = !activeTabColor ? TAB_THEME_COLOR_MAP[theme] : activeTabColor;
      this.$el.style.setProperty('--tab--active-color', colorToApply);
    }

  }
};

const __vue_script__ = script;

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "pendo-tabs",
    class: {
      'pendo-tabs--card': _vm.card,
      'pendo-tabs--vertical': _vm.vertical
    }
  }, [_c('div', {
    staticClass: "pendo-tabs-nav"
  }, [_c('span', {
    staticClass: "pendo-tabs-nav__bar"
  }), _vm._v(" "), _c('div', {
    ref: "tablist",
    staticClass: "pendo-tabs-nav__list",
    attrs: {
      "role": "tablist",
      "aria-orientation": _vm.vertical ? 'vertical' : 'horizontal'
    }
  }, _vm._l(_vm.tabs, function (tab) {
    return _c('button', {
      key: tab.prop,
      ref: "tab",
      refInFor: true,
      staticClass: "pendo-tabs-nav__tab",
      class: {
        'is-active': tab.prop === _vm.value,
        'is-disabled': tab.disabled
      },
      attrs: {
        "id": "pendo-tab-" + tab.prop,
        "aria-controls": "pendo-tab-panel-" + tab.prop,
        "aria-selected": String(tab.prop === _vm.value),
        "aria-disabled": String(!!tab.disabled),
        "title": tab.label,
        "tabindex": tab.prop === _vm.value ? undefined : -1,
        "role": "tab"
      },
      on: {
        "keyup": function ($event) {
          return _vm.onTabKeyup($event, tab);
        },
        "keydown": function ($event) {
          return _vm.onTabKeydown($event, tab);
        },
        "click": function ($event) {
          return _vm.onTabClick(tab);
        },
        "focus": function ($event) {
          return _vm.onTabFocus($event, tab);
        }
      }
    }, [_vm._t("label", [_vm._v("\n                    " + _vm._s(tab.label) + "\n                ")], {
      "tab": tab
    }), _vm._v(" "), _c('span', {
      staticClass: "pendo-tabs-nav__active-bar"
    })], 2);
  }), 0)]), _vm._v(" "), _c('div', {
    staticClass: "pendo-tabs-content"
  }, [_vm._t("default", _vm._l(_vm.tabs, function (tab) {
    return _c('pendo-tab-panel', {
      key: tab.prop,
      attrs: {
        "prop": tab.prop,
        "label": tab.label,
        "disabled": tab.disabled,
        "lazy": tab.lazy,
        "active": tab.prop === _vm.value
      }
    }, [_vm._t(tab.prop || tab.label, [_vm._v("\n                    " + _vm._s(tab.content) + "\n                ")])], 2);
  }))], 2)]);
};

var __vue_staticRenderFns__ = [];
const __vue_inject_styles__ = undefined;
const __vue_scope_id__ = undefined;
const __vue_is_functional_template__ = false;

function __vue_normalize__(template, style, script, scope, functional, moduleIdentifier, createInjector, createInjectorSSR) {
  const component = (typeof script === 'function' ? script.options : script) || {};
  component.__file = "pendo-tabs.vue";

  if (!component.render) {
    component.render = template.render;
    component.staticRenderFns = template.staticRenderFns;
    component._compiled = true;
    if (functional) component.functional = true;
  }

  component._scopeId = scope;

  return component;
}

var PendoTabs = __vue_normalize__({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__);

export default PendoTabs;
